import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  ArrowRight,
  Phone,
  MapPin,
  Clock,
  CheckCircle2,
  Hospital,
  Briefcase,
  Droplet,
  HeartPulse,
  Repeat,
  Users,
  Building2,
  AlertTriangle,
  Accessibility,
} from 'lucide-react';

const CANONICAL_PATH = '/houston-tx-medical-transportation';

const HOUSTON_FAQS = [
  {
    question: 'What areas of Houston does MoveraCare serve?',
    answer:
      "MoveraCare provides non-emergency medical transportation across Houston and Harris County, including central Houston, the Texas Medical Center, Southwest Houston, Southeast Houston, the Heights, the Galleria area, Memorial, and neighborhoods surrounding downtown. Service is subject to scheduling availability; extended-distance assignments may require advance authorization.",
  },
  {
    question: 'Do you transport patients to the Texas Medical Center?',
    answer:
      "Yes. MoveraCare provides non-emergency wheelchair and ambulatory transportation for patients traveling to appointments at hospitals and clinics located within the Texas Medical Center area, subject to scheduling availability.",
  },
  {
    question: 'Do you provide wheelchair transportation in Houston?',
    answer:
      "Yes. We operate wheelchair-accessible vehicles with proper wheelchair securement for Houston patients traveling to medical, therapy, dialysis, and rehabilitation appointments. Drivers assist passengers during transport; please share the wheelchair type, weight, and mobility level when booking.",
  },
  {
    question: 'Can Houston hospitals request discharge transportation?',
    answer:
      "Yes. Houston hospital discharge planners, case managers, and social workers can request non-emergency discharge transportation from a Houston hospital to home, a skilled nursing facility, rehabilitation, or assisted living. Same-day and scheduled discharges are supported.",
  },
  {
    question: 'Do you support recurring dialysis transportation in Houston?',
    answer:
      "Yes. Standing weekly schedules are supported for Houston dialysis patients — Monday/Wednesday/Friday, Tuesday/Thursday/Saturday, or other recurring plans coordinated with the dialysis center.",
  },
  {
    question: "Do you accept Workers' Compensation authorizations in Houston?",
    answer:
      "Yes. MoveraCare accepts authorized non-emergency transportation for injured workers in Houston traveling to Workers' Compensation-approved medical, therapy, IME, FCE, and diagnostic destinations. Authorization is determined by the applicable adjuster, case manager, TPA, or payer.",
  },
  {
    question: 'How do I book a ride from Houston?',
    answer:
      'Call dispatch at (832) 925-2128 or use the online booking form. For healthcare professionals referring a Houston patient, use the Request Patient Transportation form.',
  },
];

const HOUSTON_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'MedicalBusiness',
  '@id': 'https://www.moveracaremobility.com/houston-tx-medical-transportation#business',
  name: 'MoveraCare Mobility — Houston, TX Non-Emergency Medical Transportation',
  legalName: 'MoveraCare Services LLC',
  description:
    'Richmond, TX-based non-emergency medical transportation provider serving Houston and Harris County, Texas — wheelchair and ambulatory transportation for medical appointments, dialysis, hospital discharge, rehabilitation, and Workers’ Compensation.',
  url: 'https://www.moveracaremobility.com/houston-tx-medical-transportation',
  telephone: '+1-832-925-2128',
  email: 'dispatch@moveracaremobility.com',
  areaServed: [
    { '@type': 'City', name: 'Houston, TX' },
    { '@type': 'AdministrativeArea', name: 'Harris County, Texas' },
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Richmond',
    addressRegion: 'TX',
    addressCountry: 'US',
  },
  openingHoursSpecification: [
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'], opens: '06:00', closes: '19:00' },
    { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Sunday'], opens: '12:00', closes: '17:00' },
  ],
};

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: HOUSTON_FAQS.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

const SERVICE_TYPES = [
  { icon: Accessibility, title: 'Wheelchair Transportation', desc: 'Wheelchair-accessible transportation for Houston patients traveling to medical destinations.', to: '/services/wheelchair-transportation' },
  { icon: Users, title: 'Ambulatory Transportation', desc: 'Comfortable transport for medically stable ambulatory patients throughout Houston and Harris County.', to: '/services/ambulatory-transportation' },
  { icon: Hospital, title: 'Hospital Discharge Transportation', desc: 'Same-day and scheduled discharge transportation from Houston hospitals to home, SNF, rehab, or assisted living.', to: '/services/hospital-discharge' },
  { icon: Droplet, title: 'Dialysis Transportation', desc: 'Recurring standing schedules for Houston dialysis patients.', to: '/services/dialysis-transportation' },
  { icon: HeartPulse, title: 'Therapy & Rehabilitation Transportation', desc: 'Physical therapy, occupational therapy, and rehabilitation appointment transportation in the Houston area.', to: '/services/therapy-rehabilitation-transportation' },
  { icon: Briefcase, title: "Workers' Compensation Transportation", desc: 'Authorized transportation for Houston-area injured workers to approved medical destinations.', to: '/healthcare-partners/workers-compensation' },
  { icon: Repeat, title: 'Recurring Medical Transportation', desc: 'Standing NEMT schedules for Houston patients with ongoing treatment plans.', to: '/services/recurring-medical-transportation' },
  { icon: Clock, title: 'Wait-and-Return Transportation', desc: 'Driver-waiting service for shorter Houston-area appointments when arranged in advance.', to: '/services' },
];

const HOUSTON_AREAS = [
  'Downtown Houston',
  'Texas Medical Center',
  'Museum District',
  'Midtown',
  'The Heights',
  'Montrose',
  'Rice Village',
  'Galleria / Uptown',
  'Memorial',
  'West Houston',
  'Southwest Houston',
  'Southeast Houston',
  'East End',
  'Northside',
  'Sharpstown',
  'Meyerland',
  'Bellaire',
  'West University',
  'Braeswood',
  'Kingwood (Harris County)',
];

const NEIGHBORHOOD_ANCHORS = [
  { name: 'Texas Medical Center hospitals & clinics', desc: 'One of the largest medical complexes in the country — MoveraCare provides non-emergency wheelchair and ambulatory transportation for patients traveling to appointments in this area, subject to scheduling availability.' },
  { name: 'Houston-area dialysis centers', desc: 'Recurring weekly transportation for Houston dialysis patients with schedules aligned to treatment days and appointment windows.' },
  { name: 'Houston hospital discharge coordination', desc: 'Same-day and scheduled discharge transportation for Houston hospitals to home, SNF, rehabilitation, or assisted living for medically stable patients.' },
  { name: 'Occupational health & Workers’ Comp destinations', desc: 'Non-emergency transportation for authorized injured worker appointments across Houston, including physical therapy, IME, FCE, and occupational medicine.' },
];

const HoustonMedicalTransportation = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="houston-page">
      <Seo
        title="Non-Emergency Medical Transportation Houston TX | MoveraCare Mobility"
        description="MoveraCare Mobility provides professional wheelchair and ambulatory non-emergency medical transportation in Houston and Harris County. Call 832-925-2128 to request transportation."
        path={CANONICAL_PATH}
        schema={[HOUSTON_SCHEMA, FAQ_SCHEMA]}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Service Areas', url: '/services' },
          { name: 'Houston, TX', url: CANONICAL_PATH },
        ]}
      />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-[#004C97]" data-testid="houston-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="text-sm text-white/80 mb-6" data-testid="breadcrumbs">
            <ol className="flex flex-wrap items-center gap-1">
              <li><Link to="/" className="hover:text-[#F9A825]">Home</Link></li>
              <li className="text-white/50">/</li>
              <li><Link to="/services" className="hover:text-[#F9A825]">Service Areas</Link></li>
              <li className="text-white/50">/</li>
              <li className="text-[#F9A825] font-semibold">Houston, TX</li>
            </ol>
          </nav>
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-[#F9A825] text-[#004C97] px-4 py-1.5 rounded-full text-sm font-bold mb-6">
              <MapPin className="w-4 h-4" />
              HOUSTON · HARRIS COUNTY, TX
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Non-Emergency Medical Transportation in Houston, Texas
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              MoveraCare Mobility provides professional wheelchair and ambulatory non-emergency medical transportation for patients, families, healthcare facilities, case managers, and Workers&apos; Compensation coordinators throughout Houston and Harris County.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
              <Link to="/book">
                <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold text-lg px-8 shadow-lg" data-testid="houston-cta-book">
                  Book a Ride
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/request-patient-transportation">
                <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 shadow-lg" data-testid="houston-cta-refer">
                  Refer a Patient
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <a href="tel:+18329252128">
                <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold text-lg px-8" data-testid="houston-cta-call">
                  <Phone className="mr-2 w-5 h-5" />
                  Call Dispatch (832) 925-2128
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Houston NEMT Overview */}
      <section className="py-14 bg-white" data-testid="houston-overview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">HOUSTON NEMT OVERVIEW</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-6 leading-tight">
            Reliable Wheelchair &amp; Ambulatory Transportation for Houston Patients
          </h2>
          <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
            <p>
              Houston is home to one of the largest healthcare markets in the United States, anchored by the Texas Medical Center and served by hospitals, specialty clinics, dialysis centers, rehabilitation facilities, and outpatient providers spread across the region. Moving safely and on time between home and appointments matters — especially for patients in wheelchairs, patients recovering from surgery, dialysis patients on standing schedules, and injured workers in a Workers&apos; Compensation care plan.
            </p>
            <p>
              MoveraCare Mobility supports these patients with professional non-emergency medical transportation across Houston and Harris County. Our services include wheelchair-accessible transportation with proper wheelchair securement, comfortable ambulatory transport, recurring transportation for dialysis and therapy, hospital discharge transportation, and authorized Workers&apos; Compensation transportation. All services are provided for medically stable passengers and are scheduled subject to availability.
            </p>
            <p>
              MoveraCare is a non-emergency medical transportation provider. We do not provide ambulance, 911, stretcher ambulance, oxygen therapy, or clinical monitoring services. For medical emergencies, call 911.
            </p>
          </div>
        </div>
      </section>

      {/* Services offered */}
      <section className="py-14 bg-gray-50" data-testid="houston-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">SERVICES OFFERED IN HOUSTON</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] leading-tight">
              Non-Emergency Medical Transportation Services for Houston
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICE_TYPES.map((s) => {
              const Icon = s.icon;
              return (
                <Link key={s.title} to={s.to} className="group" data-testid={`houston-service-${s.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}>
                  <Card className="h-full border-2 hover:border-[#F9A825] transition-all">
                    <CardHeader>
                      <div className="w-11 h-11 rounded-lg bg-[#F9A825] flex items-center justify-center mb-2">
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <CardTitle className="text-[#004C97] text-base leading-tight">{s.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 leading-relaxed mb-2">{s.desc}</p>
                      <span className="text-sm font-semibold text-[#F9A825] group-hover:text-[#E08E1E]">Explore →</span>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Wheelchair & Ambulatory */}
      <section className="py-14 bg-white" data-testid="houston-wheelchair-ambulatory">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHEELCHAIR TRANSPORTATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-4 leading-tight">
                Houston Wheelchair-Accessible Transportation
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                MoveraCare operates wheelchair-accessible vehicles for Houston patients who need to remain seated in a manual or power wheelchair during transport. Drivers assist passengers during transport and follow standard wheelchair securement procedures. Door-to-door or curb-to-curb assistance is coordinated at booking. Please share the wheelchair type, weight, and any equipment needs (including oxygen) when booking so dispatch can confirm we can safely serve the trip.
              </p>
              <Link to="/services/wheelchair-transportation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]">
                Learn about Wheelchair Transportation <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
            <div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">AMBULATORY TRANSPORTATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] mb-4 leading-tight">
                Houston Ambulatory Medical Transportation
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                For medically stable passengers who can walk to and enter the vehicle independently or with limited assistance, MoveraCare provides comfortable ambulatory transportation to Houston-area medical, dental, therapy, and specialty appointments. Round-trip and wait-and-return options are available with advance scheduling.
              </p>
              <Link to="/services/ambulatory-transportation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]">
                Learn about Ambulatory Transportation <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* For Houston Healthcare Organizations */}
      <section className="py-14 bg-[#004C97]" data-testid="houston-healthcare-org">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">FOR HOUSTON HEALTHCARE ORGANIZATIONS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight">
              Transportation for Hospitals, Rehab Facilities, Dialysis Centers &amp; Case Managers
            </h2>
            <p className="text-gray-200 mt-3 max-w-3xl">
              MoveraCare works directly with Houston hospitals, rehabilitation facilities, dialysis centers, case managers, discharge planners, and social workers to coordinate reliable non-emergency medical transportation.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {NEIGHBORHOOD_ANCHORS.map((a) => (
              <div key={a.name} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5">
                <h3 className="text-white font-semibold text-lg mb-2">{a.name}</h3>
                <p className="text-gray-200 text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/healthcare-partners">
              <Button size="lg" className="bg-[#F9A825] text-[#004C97] hover:bg-[#E08E1E] font-bold" data-testid="houston-cta-healthcare-partners">
                Healthcare Partner Programs
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/healthcare-partners/hospital-discharge">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#004C97] font-semibold">
                Hospital Discharge
              </Button>
            </Link>
            <Link to="/healthcare-partners/dialysis-centers">
              <Button size="lg" variant="outline" className="border-2 border-[#F9A825] text-[#F9A825] hover:bg-[#F9A825] hover:text-[#004C97] font-semibold">
                Dialysis Centers
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Workers' Compensation for Houston */}
      <section className="py-14 bg-white" data-testid="houston-workers-comp">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div>
              <div className="w-14 h-14 rounded-lg bg-[#F9A825] flex items-center justify-center mb-3">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WORKERS&apos; COMPENSATION</p>
              <h2 className="text-2xl md:text-3xl font-bold text-[#004C97] leading-tight">
                Houston Workers&apos; Compensation Transportation
              </h2>
            </div>
            <div className="lg:col-span-2">
              <p className="text-gray-700 leading-relaxed mb-4">
                MoveraCare provides non-emergency transportation for injured workers in the Houston area attending Workers&apos; Compensation-authorized medical, therapy, IME, FCE, occupational medicine, and diagnostic appointments. We coordinate with adjusters, nurse case managers, TPAs, and ancillary service coordinators. Authorization determinations are made by the applicable payer, adjuster, case manager, or TPA — being a network vendor does not automatically guarantee that a trip is covered or payable.
              </p>
              <Link to="/healthcare-partners/workers-compensation" className="inline-flex items-center text-sm font-semibold text-[#F9A825] hover:text-[#E08E1E]" data-testid="houston-cta-workers-comp">
                Workers&apos; Compensation Transportation Details <ArrowRight className="ml-1 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-14 bg-gray-50" data-testid="houston-areas-served">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">AREAS SERVED</p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4 leading-tight">
            Houston Neighborhoods &amp; Harris County Coverage
          </h2>
          <p className="text-gray-700 mb-6 max-w-3xl">
            MoveraCare provides non-emergency medical transportation across Houston and Harris County. Services are scheduled subject to availability. Extended-distance assignments may require advance authorization.
          </p>
          <div className="flex flex-wrap gap-2">
            {HOUSTON_AREAS.map((a) => (
              <span key={a} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm text-[#004C97] font-medium">
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why choose MoveraCare */}
      <section className="py-14 bg-white" data-testid="houston-why">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">WHY CHOOSE MOVERACARE</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Built for Houston Healthcare Transportation</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Accessibility, title: 'Wheelchair-Accessible Vehicles', desc: 'Wheelchair-accessible vehicles with proper wheelchair securement. Drivers assist passengers during transport.' },
              { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport. Mobility and equipment needs are confirmed at booking.' },
              { icon: Repeat, title: 'Standing Schedules', desc: 'Reliable recurring transportation for Houston dialysis, therapy, and treatment programs.' },
              { icon: Hospital, title: 'Discharge-Ready Dispatch', desc: 'Responsive same-day and scheduled hospital discharge coordination.' },
              { icon: Briefcase, title: "Workers' Comp Coordination", desc: 'Support for authorized transportation for Houston-area injured workers.' },
              { icon: Building2, title: 'Credentialing-Ready', desc: 'Licensing, insurance, and vendor documentation available for Houston facility procurement teams.' },
            ].map((w) => {
              const Icon = w.icon;
              return (
                <div key={w.title} className="flex items-start gap-3 p-5 border-2 border-gray-100 rounded-xl hover:border-[#F9A825] transition-all">
                  <div className="w-10 h-10 rounded-lg bg-[#F9A825]/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#F9A825]" />
                  </div>
                  <div>
                    <h3 className="text-[#004C97] font-semibold mb-1 leading-tight">{w.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{w.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 bg-gray-50" data-testid="houston-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-xs font-bold tracking-widest text-[#F9A825] mb-2">HOUSTON FAQ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004C97]">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-3">
            {HOUSTON_FAQS.map((f) => (
              <details key={f.question} className="bg-white border-2 border-gray-100 rounded-lg p-5 group">
                <summary className="font-semibold text-[#004C97] cursor-pointer flex items-center justify-between">
                  <span>{f.question}</span>
                  <span className="text-[#F9A825] transition-transform group-open:rotate-90">→</span>
                </summary>
                <p className="mt-3 text-sm text-gray-700 leading-relaxed">{f.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-14 bg-[#F9A825]" data-testid="houston-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#004C97] mb-4">
            Request Houston Medical Transportation
          </h2>
          <p className="text-lg text-[#004C97]/85 mb-8">
            Book a Houston wheelchair or ambulatory ride, refer a patient, or reach dispatch directly.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center">
            <Link to="/book">
              <Button size="lg" className="bg-[#004C97] text-white hover:bg-[#003D7A] font-semibold text-lg px-8 py-6 shadow-lg" data-testid="houston-final-book">
                Book a Ride
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/request-patient-transportation">
              <Button size="lg" className="bg-white text-[#004C97] hover:bg-gray-100 font-semibold text-lg px-8 py-6 shadow-lg" data-testid="houston-final-refer">
                Refer a Patient
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:+18329252128">
              <Button size="lg" variant="outline" className="border-2 border-[#004C97] text-[#004C97] hover:bg-[#004C97] hover:text-white font-semibold text-lg px-8 py-6" data-testid="houston-final-call">
                <Phone className="mr-2 w-5 h-5" />
                (832) 925-2128
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Non-emergency disclaimer */}
      <section className="py-6 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-100 border-l-4 border-gray-400 rounded p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-700 leading-relaxed">
              MoveraCare provides <strong>non-emergency medical transportation</strong> for medically stable passengers in Houston and Harris County. We do not provide ambulance, 911, emergency response, stretcher ambulance, oxygen therapy, or clinical monitoring services. <strong>Not for medical emergencies. Call 911 in an emergency.</strong>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HoustonMedicalTransportation;
